import "../../../static/css/extension.css";
import {type ChangeEvent, type FormEvent, useCallback, useEffect, useState} from "react";
import {BlueshiftAPIConnectorError} from "../../../lib/api.ts";
import {et} from "../../../locales/i18n.tsx";
import {extension, ExtensionAPIConnectorError} from "../../../lib/Extension.ts";
import {swal_util} from "../../../lib/SwalUtil.ts";
import {useTranslation} from "react-i18next";
import {parseMultilingualMarkdown} from "../../../util/MarkdownParser.ts";

interface RenewalProps {
    expirationDate?: string,
    userId: string
    onSubmit?: (val: boolean) => void,
}

export default function Extension({onSubmit, expirationDate, userId}: RenewalProps) {
    const {t} = useTranslation('ui');
    const { i18n } = useTranslation();
    const [checked, setChecked] = useState<{ [key: string]: boolean }>({});
    const [data, setData] = useState<{
        extension_days: string,
        terms_of_use: string,
        privacy_policy: string
    }>({
        extension_days: "",
        terms_of_use: "",
        privacy_policy: ""
    });

    const [reason, setReason] = useState("");
    const [termsHtml, setTermsHtml] = useState("");
    const [privacyHtml, setPrivacyHtml] = useState("");

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const next = e.target.value.slice(0, 300);
        setReason(next);
    };


    function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>) {
        const name = event.target.name;
        setChecked(prev => ({
            ...prev,
            [name]: !prev[name]
        }));
    }


    const init = useCallback(async () => {
        try {
            const res = await extension.extensionInfo();
            setData(res);
        } catch (err) {
            if (err instanceof BlueshiftAPIConnectorError) {
                throw new ExtensionAPIConnectorError(err, et(err.cause));
            }
            throw err;
        }
    }, []);

    const onSubmitExtensionForm = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            if (reason === "") {
                await swal_util.show_error(t("extension.enterReason")); // "연장 사유를 작성해주세요."
                return false;
            }

            if (!checked["terms_of_use"] || !checked["privacy_policy"]) {
                await swal_util.show_error(t("signUp.error.acceptTerms")); // 공통 재사용
                return false;
            }

            try {
                await extension.applicationForExtension(userId, reason);
            } catch (e) {
                if (e instanceof BlueshiftAPIConnectorError) {
                    throw new ExtensionAPIConnectorError(e, et(e.cause));
                }
                await swal_util.show_error(t("extension.failedToExtendPeriod"));


                throw e;
            }

            await swal_util.show_info(t("extension.applicationForExtensionOfPeriodSubmitted"));
            await init();
            onSubmit?.(true);
        },
        [checked, init, onSubmit, reason, t, userId]
    );


    useEffect(() => {
        init().finally();
    }, [init]);
    useEffect(() => {
        async function renderMarkdown() {
            setTermsHtml(await parseMultilingualMarkdown(data.terms_of_use || "", i18n.language));
            setPrivacyHtml(await parseMultilingualMarkdown(data.privacy_policy || "", i18n.language));
        }

        renderMarkdown().finally();
    }, [data.terms_of_use, data.privacy_policy, i18n.language]);

    return <>
        <div style={{margin: "0 auto", marginLeft: "544px"}}>
            <div className="text-start"><h3 className="text-secondary">{t("extension.extendPeriod")}</h3></div>
            <div className="form-box">
                <form className="custom-validation" id="form_renewal" action="#" onSubmit={onSubmitExtensionForm}>
                    <div className="signup_container">
                        <label htmlFor="expiration_date">{t("extension.expirationDate")}</label>
                        <input type="date"
                               name="expiration_date"
                               className="form-control rounded-pill bg-light px-4"
                               autoComplete="username"
                               value={expirationDate ? expirationDate.split(" ")[0] : ""}
                               readOnly/>
                    </div>
                    <div className="signup_container">
                        <label htmlFor="extension_days">{t("extension.extensionDays")}</label>
                        <input type="text"
                               id="extension_days"
                               name="extension_days"
                               className="form-control rounded-pill bg-light px-4"
                               value={data.extension_days}
                               readOnly/>
                    </div>

                    <h6 className="div_input text-primary">
                        <label htmlFor="terms_of_use">{t("extension.reasonLabel")}</label>
                    </h6>
                    <div>
                        <textarea className="form-control px-2" value={reason}
                                  onChange={handleChange}
                                  maxLength={300}
                                  placeholder={t("extension.reasonPlaceholder")}/>
                        <div className="small_text">
                            {reason.length} / 300
                        </div>
                    </div>

                    <div className="div_input col-6 pt-2">
                        <input type="checkbox" className="form-check-input"
                               id="terms_of_use"
                               name="terms_of_use"
                               checked={checked["terms_of_use"] ?? false}
                               onChange={handleCheckboxChange}/>
                        <h6 className="text-primary">
                            <label htmlFor="terms_of_use">{t("signUp.termsOfUse")} ({t("signUp.required")})</label>
                        </h6>
                    </div>
                    <div id="r_text_box"
                         dangerouslySetInnerHTML={{__html: termsHtml}}></div>
                    <div className="div_input col-6">
                        <input type="checkbox" className="form-check-input"
                               name="privacy_policy"
                               id="privacy_policy"
                               checked={checked["privacy_policy"] ?? false}
                               onChange={handleCheckboxChange}/>
                        <h6 className="text-primary">
                            <label htmlFor="privacy_policy">{t("signUp.privacyPolicy")} ({t("signUp.required")})</label>
                        </h6>
                    </div>

                    <div id="r_text_box"
                         dangerouslySetInnerHTML={{__html: privacyHtml}}></div>
                    <div className="d-flex gap-3 mt-3">
                        <button
                            type="button" className="button_red rounded-4 flex-grow-1 mb-3"
                                onClick={() => onSubmit?.(false)}
                        >{t("common.cancel", { defaultValue: "취소" })}
                        </button>
                        <button type="submit"
                                className="button_blue rounded-4 flex-grow-1 mb-3">{t("common.confirm", { defaultValue: "확인" })}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </>
}